import { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, subCategories, getMoviesByCategory, siteInfo } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSub, setActiveSub] = useState<string>('all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const category = useMemo(() => categories.find(c => c.slug === slug), [slug]);
  const allMovies = useMemo(() => getMoviesByCategory(slug || ''), [slug]);
  const subs = useMemo(() => subCategories[slug || ''] || [], [slug]);
  const breadcrumbItems = useMemo(() => getCategoryBreadcrumb(slug || ''), [slug]);

  const filtered = activeSub === 'all' ? allMovies : allMovies.filter(m => m.subCategory === activeSub);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-600">分类不存在</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          {/* Category Header */}
          <div className="px-4 md:px-6 py-6 md:py-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className={`${category.icon} text-xl md:text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">{category.name}</h1>
                <p className="text-sm text-foreground-600 mt-1">
                  51影院{category.name}频道 — 超清画质 · 免广告 · 流畅在线观看
                </p>
              </div>
            </div>
          </div>

          {/* Subcategory Tabs */}
          {subs.length > 0 && (
            <div className="px-4 md:px-6 pb-6">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setActiveSub('all')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSub === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </button>
                {subs.map(sub => (
                  <button
                    key={sub.slug}
                    onClick={() => setActiveSub(sub.slug)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSub === sub.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Movies Grid */}
          <div className="px-4 md:px-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-600">共 <strong>{filtered.length}</strong> 部作品</p>
            </div>
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-10">
                {filtered.map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-foreground-500">
                <i className="ri-inbox-line text-4xl block mb-3"></i>
                暂无该分类的影视资源
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}